/**
 * @file InfoElementPanels.tsx
 * @description Панели «Вопросы и ответы», «Контакты», «Подписка»
 * (phase19.16 Р2; uiux-design-r2 §4; SB25, SB26): вопросы — список «вопрос
 * — ответ» до 30 с перетаскиванием и «первый открыт»; контакты — отметки
 * из «Настроек сайта» и ссылка «Изменить контакты →» (вписать нельзя);
 * подписка — пояснение: галочки согласия рисует сайт сам.
 *
 * phase19.28 этап А: в «Контактах» — и производные виды (`hours` «Часы
 * работы» из поля настроек сайта): подпись — `sectionContactKindLabel`,
 * производные — отдельными строками во всю ширину с подсказкой реестра;
 * ссылка «Изменить контакты →» одна (поле «Часы работы» — в том же разделе).
 */

import {
  SECTION_CONTACT_DERIVED_KINDS,
  SECTION_CONTACT_KIND_KEYS,
  SECTION_TEXT_LIMITS,
  SITE_LIMITS,
  isSectionContactDerivedKind,
  sectionContactKindLabel,
  type SectionContactKind,
  type SectionElementOf,
} from "@chaiberi/shared";

import { cn } from "@/lib/utils";
import { JUMP_LINK_CLASS } from "../../../ui/jump-link";
import { CheckRow } from "../../../site-view/CheckRow";
import { SectionTextField } from "../fields/SectionTextField";
import { SortableList } from "../fields/SortableList";
import { useSectionForm } from "../section-form-context";
import { useBuilder } from "../../builder-context";
import type { ElementPanelProps } from "./types";

type Faq = SectionElementOf<"faq">["items"][number];

export function FaqPanel({ element, onChange, path }: ElementPanelProps<"faq">) {
  const { hasIssuesUnder } = useSectionForm();
  return (
    <>
      <SortableList<Faq>
        label="Вопросы"
        items={element.items}
        max={SITE_LIMITS.faqItems}
        addLabel="Вопрос"
        makeItem={() => ({ q: "Вопрос покупателя", a: "Ответ." })}
        onChange={(items) => onChange({ ...element, items })}
        invalidAt={(i) => hasIssuesUnder(`${path}.items.${i}`)}
        summary={(item) => item.q || "Без вопроса"}
        renderItem={(item, update, i) => (
          <>
            <SectionTextField label="Вопрос" path={`${path}.items.${i}.q`} value={item.q} max={SECTION_TEXT_LIMITS.faqQuestion} onChange={(q) => update({ ...item, q })} />
            <SectionTextField label="Ответ" path={`${path}.items.${i}.a`} value={item.a} max={SECTION_TEXT_LIMITS.faqAnswer} multiline onChange={(a) => update({ ...item, a })} />
          </>
        )}
      />
      <CheckRow checked={element.firstOpen} onChange={(firstOpen) => onChange({ ...element, firstOpen })}>
        Первый вопрос открыт
      </CheckRow>
    </>
  );
}

export function ContactsPanel({ element, onChange, path }: ElementPanelProps<"contacts">) {
  const b = useBuilder();
  const { issueAt } = useSectionForm();
  const listKinds = SECTION_CONTACT_KIND_KEYS.filter((kind) => !isSectionContactDerivedKind(kind));
  const derivedKinds = SECTION_CONTACT_KIND_KEYS.filter(isSectionContactDerivedKind);
  const toggle = (kind: SectionContactKind, on: boolean) =>
    onChange({ ...element, kinds: on ? [...element.kinds, kind] : element.kinds.filter((k) => k !== kind) });
  return (
    <>
      <p className="type-caption text-on-surface-variant">Контакты берутся из «Настроек сайта». Отметьте, какие показать; не заполненные там на сайте не появятся.</p>
      <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
        {listKinds.map((kind) => (
          <CheckRow key={kind} checked={element.kinds.includes(kind)} onChange={(on) => toggle(kind, on)}>
            {sectionContactKindLabel(kind)}
          </CheckRow>
        ))}
      </div>
      {/* Производные виды — во всю ширину панели, подсказка не зажата колонкой. */}
      {derivedKinds.map((kind) => (
        <div key={kind} className="flex flex-col">
          <CheckRow checked={element.kinds.includes(kind)} onChange={(on) => toggle(kind, on)}>
            {sectionContactKindLabel(kind)}
          </CheckRow>
          <p className="type-caption pl-7 text-on-surface-variant">{SECTION_CONTACT_DERIVED_KINDS[kind].hint}</p>
        </div>
      ))}
      {issueAt(`${path}.kinds`) ? <p className="type-caption text-destructive">{issueAt(`${path}.kinds`)}</p> : null}
      <button type="button" className={cn(JUMP_LINK_CLASS, "self-start text-sm")} onClick={() => void b.select({ panel: "settings", sub: "contacts" })}>
        Изменить контакты →
      </button>
    </>
  );
}

export function NewsletterPanel(_props: ElementPanelProps<"newsletter">) {
  return (
    <p className="type-body text-on-surface-variant">
      Поле «Подписаться на новости» с кнопкой. Галочки согласия на рассылку и на обработку данных сайт добавит сам — их текст задаёт платформа.
    </p>
  );
}
